// Per-tile bento cell + chrome (TASK-493, ports SixEase CanvasTile, re-tokenised).
// Chrome is an absolutely-positioned overlay revealed on hover, so it wraps ANY
// widget unedited. SELF_CONTAINED widgets own their padded card, so the body is
// left unpadded for them to avoid a double inset.
import type { ReactNode } from 'react';
import { ArrowLeftToLine, ChevronDown, ChevronUp, Maximize2, Minimize2, X } from 'lucide-react';
import type { CanvasTileSpec } from '../../registry/types';
import { MAXH_CLASS, SPAN_CLASS, SELF_CONTAINED } from '../../registry/tileLayout';
import { cn } from '../../lib/utils';

interface CanvasTileProps {
  tile: CanvasTileSpec;
  maximized: boolean;
  onToggleCollapse: (id: string) => void;
  onToggleMaximize: (id: string) => void;
  onSetRail: (id: string, rail: 'left' | null) => void;
  onClose: (id: string) => void;
  children: ReactNode;
}

const ICON = 'h-3.5 w-3.5';
const BTN =
  'flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground';

export function CanvasTile({
  tile,
  maximized,
  onToggleCollapse,
  onToggleMaximize,
  onSetRail,
  onClose,
  children,
}: CanvasTileProps) {
  const selfContained = SELF_CONTAINED.has(tile.component);
  return (
    <div
      id={tile.id}
      className={cn(
        'group relative flex min-h-0 min-w-0 flex-col rounded-2xl border border-border bg-card transition-shadow',
        // Maximize fills the viewport ABOVE the input dock (TASK-650): bottom-40
        // reserves the dock's height so the tile's final row stays reachable.
        maximized ? 'fixed left-4 right-4 top-4 bottom-40 z-40 overflow-auto shadow-2xl' : SPAN_CLASS[tile.size],
        tile.rail && 'ring-1 ring-kielis-blue/40',
      )}
    >
      {/* TASK-651: an in-place update (repeated request) is declared on the tile. */}
      {tile.updatedAt && (
        <span
          data-testid="tile-updated-badge"
          className="absolute left-2 top-2 z-10 rounded-full bg-status-info/10 px-2 py-0.5 text-[10px] font-medium text-status-info-fg"
        >
          Updated{' '}
          {new Date(tile.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      )}

      {/* Overlay chrome — top-right, reveals on hover (or while pinned/maximized) */}
      <div
        className={cn(
          'absolute right-2 top-2 z-10 flex items-center gap-0.5 rounded-lg border border-border bg-muted/90 px-0.5 py-0.5 opacity-0 shadow-sm backdrop-blur-sm transition-opacity group-hover:opacity-100 focus-within:opacity-100',
          (tile.rail || maximized) && 'opacity-100',
        )}
      >
        <button
          type="button"
          className={BTN}
          title={tile.collapsed ? 'Expand' : 'Collapse'}
          aria-label={tile.collapsed ? 'Expand tile' : 'Collapse tile'}
          onClick={() => onToggleCollapse(tile.id)}
        >
          {tile.collapsed ? <ChevronDown className={ICON} /> : <ChevronUp className={ICON} />}
        </button>
        <button
          type="button"
          className={BTN}
          title={maximized ? 'Restore' : 'Maximize'}
          aria-label={maximized ? 'Restore tile' : 'Maximize tile'}
          onClick={() => onToggleMaximize(tile.id)}
        >
          {maximized ? <Minimize2 className={ICON} /> : <Maximize2 className={ICON} />}
        </button>
        <button
          type="button"
          className={cn(BTN, tile.rail === 'left' && 'text-kielis-blue hover:text-kielis-blue')}
          title={tile.rail === 'left' ? 'Unpin from rail' : 'Pin to rail'}
          aria-label={tile.rail === 'left' ? 'Unpin from rail' : 'Pin to rail'}
          aria-pressed={tile.rail === 'left'}
          onClick={() => onSetRail(tile.id, tile.rail === 'left' ? null : 'left')}
        >
          <ArrowLeftToLine className={ICON} />
        </button>
        <button
          type="button"
          className={cn(BTN, 'hover:text-destructive')}
          title="Close"
          aria-label="Close tile"
          onClick={() => onClose(tile.id)}
        >
          <X className={ICON} />
        </button>
      </div>

      {/* Body — hidden when collapsed (clamp to a thin header strip). A maximized
          tile always shows content: a collapsed full-viewport frame is useless. */}
      {tile.collapsed && !maximized ? (
        <div className="h-10" aria-hidden />
      ) : (
        <div
          className={cn(
            'flex-1 overflow-y-auto',
            // TASK-601: cap tile height per size class — overflow scrolls inside
            // the tile body. Maximized tiles get the full viewport instead.
            !maximized && MAXH_CLASS[tile.size],
            !selfContained && 'p-4',
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}
